import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus, SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/vroom/chip";
import { RolePill } from "@/components/vroom/role-pill";
import { StatusBadge } from "@/components/vroom/status-badge";
import { UserIdentityCell } from "@/components/ui/avatar";
import { DataTable } from "@/components/wrapped/data-table";
import { EmptyState } from "@/components/vroom/empty-state";
import { SearchBar } from "@/components/vroom/search-bar";
import { AdminShell } from "@/components/layout/admin-shell";
import { splitName } from "@/lib/split-name";
import {
  IndexPageHeader,
  IndexPageHeaderTitle,
  IndexPageHeaderToolbar,
} from "@/components/vroom/index-page-header";
import { appSidebarSections, appBottomNavItems } from "@/lib/app-sidebar";
import { usersListOptions, usersFacetsOptions } from "@/api/users/query-options";
import { useResendInvite } from "@/api/users/mutations";
import { UserEditSheet } from "@/components/admin/user-edit-sheet";
import { UserCreateSheet } from "@/components/admin/user-create-sheet";
import { InviteLinkDialog } from "@/components/admin/invite-link-dialog";
import type { UserAdmin } from "@/api/generated/types.gen";

export const usersSearchSchema = z.object({
  role: z.enum(["worker", "approver", "super_admin"]).optional(),
  status: z.enum(["active", "inactive"]).optional(),
  erpLinked: z.enum(["true", "false"]).optional(),
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
});

function buildUsersListParams(search: z.infer<typeof usersSearchSchema>) {
  return {
    role: search.role,
    isActive:
      search.status === "active"
        ? ("true" as const)
        : search.status === "inactive"
          ? ("false" as const)
          : undefined,
    erpLinked: search.erpLinked,
    q: search.q,
    page: search.page,
    pageSize: 50,
  };
}

export const Route = createFileRoute("/_authed/admin/users")({
  beforeLoad: ({ context }) => {
    const user = (context as { user?: { role?: string } }).user;
    if (user?.role !== "super_admin") throw redirect({ to: "/dashboard" });
  },
  validateSearch: usersSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const qc = (context as { queryClient: QueryClient }).queryClient;
    void qc.prefetchQuery(usersListOptions(buildUsersListParams(deps)));
    void qc.prefetchQuery(usersFacetsOptions());
  },
  component: UsersAdminPage,
});

interface SessionUser {
  name: string;
  email: string;
  role?: "worker" | "approver" | "super_admin";
}

export function UsersAdminPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { user: sessionUser } = Route.useRouteContext() as { user: SessionUser };
  const { firstName, lastName } = splitName(sessionUser.name);

  // Debounced q.
  const [qLocal, setQLocal] = useState(search.q ?? "");
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (qLocal !== (search.q ?? "")) {
        navigate({
          to: "/admin/users",
          search: (s) => ({ ...s, q: qLocal || undefined, page: 1 }),
        });
      }
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [qLocal, navigate, search.q]);

  const queryParams = buildUsersListParams(search);
  const list = useQuery(usersListOptions(queryParams));
  const facets = useQuery(usersFacetsOptions());

  const [editingUser, setEditingUser] = useState<UserAdmin | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteDialog, setInviteDialog] = useState<null | {
    email: string;
    url: string;
    emailDeliveryFailed: boolean;
  }>(null);

  const resendMut = useResendInvite();

  const items = (list.data?.data ?? []) as UserAdmin[];

  async function resendInvite(id: string) {
    const target = items.find((u) => u.id === id);
    if (!target) return;
    try {
      const result = await resendMut.mutateAsync({ id });
      setInviteDialog({
        email: target.email,
        url: result.invite.url,
        emailDeliveryFailed: result.invite.emailDeliveryFailed,
      });
    } catch {
      toast.error(t("admin.users.edit-sheet.toast.resend-failed"));
    }
  }

  function toggleChip(patch: Partial<typeof search>) {
    navigate({
      to: "/admin/users",
      search: (s) => ({ ...s, ...patch, page: 1 }),
    });
  }

  const f = facets.data;
  const allCount = f ? f.roles.total : undefined;
  const workerCount = f ? f.roles.worker : undefined;
  const approverCount = f ? f.roles.approver : undefined;
  const superCount = f ? f.roles.super_admin : undefined;
  const inactiveCount = f ? f.status.inactive : undefined;
  const unlinkedCount = f ? f.erpLinked.unlinked : undefined;

  const isAllActive = !search.role && !search.status && !search.erpLinked;

  const columns: ColumnDef<UserAdmin, unknown>[] = [
    {
      accessorKey: "name",
      header: t("admin.users.columns.name.header"),
      cell: (info) => {
        const u = info.row.original;
        return <UserIdentityCell name={u.name} email={u.email} />;
      },
    },
    {
      accessorKey: "role",
      header: t("admin.users.columns.role.header"),
      cell: (info) => <RolePill role={info.getValue<"worker" | "approver" | "super_admin">()} />,
    },
    {
      accessorKey: "defaultApproverId",
      header: t("admin.users.columns.default-approver.header"),
      cell: (info) => {
        const u = info.row.original;
        if (u.role !== "worker") return <span className="text-ink-500">—</span>;
        if (!u.defaultApproverId)
          return (
            <span className="text-[12.5px] text-vroom-red">
              {t("admin.users.columns.default-approver.not-set")}
            </span>
          );
        return <span className="text-[12.5px] text-ink-700">{u.defaultApproverId}</span>;
      },
    },
    {
      accessorKey: "erpEmployeeNumber",
      header: t("admin.users.columns.erp.header"),
      cell: (info) => {
        const u = info.row.original;
        if (u.role === "super_admin" && !u.erpEmployeeNumber)
          return (
            <span className="text-[12.5px] text-ink-500">
              {t("admin.users.columns.erp.entra-only")}
            </span>
          );
        if (!u.erpEmployeeNumber)
          return (
            <span className="text-[12.5px] text-vroom-red">
              {t("admin.users.columns.erp.not-linked")}
            </span>
          );
        return <span className="font-mono text-[12px] text-ink-700">{u.erpEmployeeNumber}</span>;
      },
    },
    {
      accessorKey: "isActive",
      header: t("admin.users.columns.status.header"),
      cell: (info) => <StatusBadge status={info.getValue<boolean>() ? "ok" : "rej"} />,
    },
    {
      id: "invite",
      header: t("admin.users.columns.invite.header"),
      cell: (info) => {
        const u = info.row.original;
        if (u.inviteStatus === "pending") {
          return (
            <Button variant="ghost" onClick={() => resendInvite(u.id)}>
              {t("admin.users.columns.invite.pending-resend")}
            </Button>
          );
        }
        if (u.inviteStatus === "expired") {
          return (
            <Button variant="ghost" className="text-vroom-red" onClick={() => resendInvite(u.id)}>
              {t("admin.users.columns.invite.expired-resend")}
            </Button>
          );
        }
        return <span className="text-ink-500">—</span>;
      },
    },
    {
      id: "actions",
      header: "",
      cell: (info) => (
        <Button variant="ghost" onClick={() => setEditingUser(info.row.original)}>
          {t("admin.users.columns.actions.edit.label")}
        </Button>
      ),
    },
    // TODO: kostenplaats column pending API extension.
  ];

  return (
    <>
      <AdminShell
        user={{ firstName, lastName, role: "super_admin" }}
        section="admin"
        breadcrumb={<>{t("admin.users.breadcrumb")}</>}
        sidebarSections={appSidebarSections({ active: "users", role: "super_admin" }, t)}
        bottomNavItems={appBottomNavItems(
          { section: "admin", active: "users", role: "super_admin" },
          t,
        )}
      >
        <IndexPageHeader>
          <IndexPageHeaderTitle
            title={t("admin.users.title")}
            kicker={
              allCount !== undefined ? t("admin.users.kicker-total", { count: allCount }) : ""
            }
          />
          <IndexPageHeaderToolbar>
            <div className="flex flex-wrap items-center gap-2">
              <Chip
                variant={isAllActive ? "active" : "default"}
                onClick={() => navigate({ to: "/admin/users", search: { page: 1 } })}
              >
                {t("admin.users.chips.all.label")}{" "}
                {allCount !== undefined ? (
                  <span className="ml-1 text-ink-500">{allCount}</span>
                ) : null}
              </Chip>
              <Chip
                variant={search.role === "worker" ? "active" : "default"}
                onClick={() =>
                  toggleChip({ role: "worker", status: undefined, erpLinked: undefined })
                }
              >
                {t("admin.users.chips.workers.label")}{" "}
                {workerCount !== undefined ? (
                  <span className="ml-1 text-ink-500">{workerCount}</span>
                ) : null}
              </Chip>
              <Chip
                variant={search.role === "approver" ? "active" : "default"}
                onClick={() =>
                  toggleChip({ role: "approver", status: undefined, erpLinked: undefined })
                }
              >
                {t("admin.users.chips.approvers.label")}{" "}
                {approverCount !== undefined ? (
                  <span className="ml-1 text-ink-500">{approverCount}</span>
                ) : null}
              </Chip>
              <Chip
                variant={search.role === "super_admin" ? "active" : "default"}
                onClick={() =>
                  toggleChip({ role: "super_admin", status: undefined, erpLinked: undefined })
                }
              >
                {t("admin.users.chips.super-admins.label")}{" "}
                {superCount !== undefined ? (
                  <span className="ml-1 text-ink-500">{superCount}</span>
                ) : null}
              </Chip>
              <Chip
                variant={search.status === "inactive" ? "active" : "default"}
                onClick={() =>
                  toggleChip({ status: "inactive", role: undefined, erpLinked: undefined })
                }
              >
                {t("admin.users.chips.inactive.label")}{" "}
                {inactiveCount !== undefined ? (
                  <span className="ml-1 text-ink-500">{inactiveCount}</span>
                ) : null}
              </Chip>
              <Chip
                variant={search.erpLinked === "false" ? "active" : "default"}
                onClick={() =>
                  toggleChip({ erpLinked: "false", role: undefined, status: undefined })
                }
              >
                {t("admin.users.chips.erp-unlinked.label")}{" "}
                {unlinkedCount !== undefined ? (
                  <span className="ml-1 text-ink-500">{unlinkedCount}</span>
                ) : null}
              </Chip>
            </div>
            <div className="flex items-center gap-2">
              <SearchBar value={qLocal} onChange={(e) => setQLocal(e.target.value)} />
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-1 size-4" /> {t("admin.users.actions.invite.label")}
              </Button>
            </div>
          </IndexPageHeaderToolbar>
        </IndexPageHeader>
        <div className="px-4 py-4 md:px-6 md:py-6">
          {items.length === 0 ? (
            <EmptyState
              variant="filtered"
              icon={<SearchX size={20} />}
              title={t("admin.users.empty.title")}
              description={t("admin.users.empty.description")}
            />
          ) : (
            <DataTable data={items} columns={columns} />
          )}
        </div>
      </AdminShell>

      {editingUser ? (
        <UserEditSheet
          open
          user={editingUser}
          onOpenChange={(open) => !open && setEditingUser(null)}
          approverCandidates={items
            .filter((u) => u.role === "approver" || u.role === "super_admin")
            .map((u) => ({ id: u.id, name: u.name, email: u.email }))}
        />
      ) : null}

      <UserCreateSheet open={createOpen} onOpenChange={setCreateOpen} />

      {inviteDialog ? (
        <InviteLinkDialog
          open
          onOpenChange={(v) => !v && setInviteDialog(null)}
          email={inviteDialog.email}
          url={inviteDialog.url}
          emailDeliveryFailed={inviteDialog.emailDeliveryFailed}
        />
      ) : null}
    </>
  );
}
